import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";

interface AcademicDetailsTabProps {
  academicDetails: any[];
  isMobile: boolean;
  educationGap?: number;
}

const AcademicDetailsTab: React.FC<AcademicDetailsTabProps> = ({
  academicDetails,
  isMobile,
  educationGap,
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );
  const [educationGapValue, setEducationGapValue] = React.useState<
    string | undefined
  >();

  React.useEffect(() => {
    // Extract educationGap from Redux data when available
    if (fetchUserApplicationData) {
      const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
        (h: any) => h.horizontalStepName === "Applicant & Loan Details"
      );
      const academicDetailsStep = applicantLoanStep?.verticalSteps?.find(
        (v: any) => v.verticalStepName === "Academic Details"
      );

      if (academicDetailsStep?.fields) {
        const educationGapField = academicDetailsStep.fields.find(
          (fieldObj: any) => Object.keys(fieldObj)?.[0] === "educationGap"
        );
        if (educationGapField) {
          const value = educationGapField["educationGap"];
          setEducationGapValue(value);
        }
      }
    }
  }, [fetchUserApplicationData]);

  // Helper to map academic details from the vertical step to the required format
  function mapAcademicDetailsFromVerticalStep(verticalStep: any): {
    courses: { [key: string]: any[] };
    educationGap?: any;
  } {
    if (!verticalStep || !verticalStep?.fields) return { courses: {} };
    // Group fields by course number
    const grouped: { [key: string]: any[] } = {};
    let extractedEducationGap: any;

    verticalStep?.fields.forEach((fieldObj: any) => {
      const key = Object.keys(fieldObj)?.[0];
      const value = fieldObj?.[key];

      // Extract educationGap value
      if (key === "educationGap") {
        extractedEducationGap = value;
      }

      // Try to extract course number from key (e.g., course1Type, course2Cgpa, etc.)
      const match = key.match(/course(\d+)/);
      if (match) {
        const courseNum = match[1];
        if (!grouped[courseNum]) grouped[courseNum] = [];
        grouped[courseNum].push({
          label:
            key
              .replace(/course\d+/, "")
              .replace(/([A-Z])/g, " $1")
              .trim() || key,
          key,
          value,
        });
      }
    });
    return { courses: grouped, educationGap: extractedEducationGap };
  }

  // Helper to map academic details from prop array format
  function mapAcademicDetailsFromProp(details: any[]): {
    [key: string]: any[];
  } {
    if (!details || details.length === 0) return {};
    const grouped: { [key: string]: any[] } = {};

    details.forEach((fieldObj: any) => {
      const { key, label, value } = fieldObj;
      const match = key.match(/course(\d+)/);
      if (match) {
        const courseNum = match[1];
        if (!grouped[courseNum]) grouped[courseNum] = [];
        grouped[courseNum].push({ label, key, value });
      }
    });
    return grouped;
  }

  let groupedCourses: { [key: string]: any[] } = {};

  // First priority: use academicDetails prop if available
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    academicDetails &&
    academicDetails.length > 0
  ) {
    groupedCourses = mapAcademicDetailsFromProp(academicDetails);
  }

  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Academic Details' vertical step
    const academicDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Academic Details"
    );
    const result = mapAcademicDetailsFromVerticalStep(academicDetailsStep);
    groupedCourses = result.courses;
  }

  if (!groupedCourses || Object.keys(groupedCourses).length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const capitalizeValue = (val: any) => {
    if (val === null || val === undefined) return "";

    return String(val)
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderSectionHeading = (title: string, marginTop: number) => (
    <Grid item xs={12} sx={{ mt: marginTop, mb: 1 }}>
      <Box
        sx={{
          backgroundColor: "#edf6f6",
          borderLeft: "3px solid #00b3b5",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "15px", fontWeight: 600, color: "#6f6b7d" }}
        >
          {title}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <>
      <Grid item xs={12} sm={6} md={3}>
        <DetailRow
          item={{
            label: "Education Gap",
            value: fetchUserApplicationData?.partnerStartedJourney
              ? educationGap
              : educationGapValue,
            isVerified: false,
          }}
        />
      </Grid>

      {Object.entries(groupedCourses).map(([courseNum, fields]) => (
        <React.Fragment key={courseNum}>
          {renderSectionHeading(
            `Qualification ${courseNum}`,
            Number(courseNum) > 1 ? (isMobile ? 4 : 2) : 1,
          )}
          {(fields as any[])?.map((field: any, subIndex: number) => {
            const skipNames = [
              `course${courseNum}ScoreType`.toLowerCase(),
              `course${courseNum}TypeID`.toLowerCase(),
            ];
            if (skipNames.includes((field.key || "").toLowerCase()))
              return null;

            // Replace CGPA label with Percentage and remove "Course" from "Course Type"
            let displayLabel = field.label;

            if (
              (field.key || "").toLowerCase() ===
              `course${courseNum}Cgpa`.toLowerCase()
            ) {
              displayLabel = "Percentage";
            } else if (
              (field.key || "").toLowerCase() ===
              `course${courseNum}Type`.toLowerCase()
            ) {
              displayLabel = "Type";
            }

            return (
              <Grid item xs={12} sm={6} md={3} key={`${courseNum}-${subIndex}`}>
                <DetailRow
                  item={{
                    label: displayLabel,
                    value: capitalizeValue(field.value),
                    isVerified: false,
                  }}
                />
              </Grid>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export default AcademicDetailsTab;
